import { useCallback, useEffect, useRef, useState } from 'react';
import { debounce } from '@/utils/debounce';

export const EXAMPLE_KEYS = [
  'example1',
  'example2',
  'example3',
  'example4',
  'example5',
] as const;

export const EXAMPLE_IMAGES = [
  'shop1',
  'shop2',
  'shop3',
  'shop4',
  'shop5',
] as const;

const SCROLL_STEP_PX = 700;
const SCROLL_STEP_MIN_PX = 200;
const SCROLL_STEP_RATIO = 0.8;

function computeScrollStep(width: number): number {
  return Math.min(
    SCROLL_STEP_PX,
    Math.max(SCROLL_STEP_MIN_PX, Math.round(width * SCROLL_STEP_RATIO))
  );
}

export function useExamplesScroll(isLarge: boolean) {
  const wrapperRef = useRef<HTMLDivElement>(null);
  const stepRef = useRef(SCROLL_STEP_PX);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(false);

  const updateScrollState = useCallback(() => {
    const el = wrapperRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 0);
    setCanScrollRight(el.scrollLeft + el.clientWidth < el.scrollWidth);
  }, []);

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;

    const syncStep = () => {
      stepRef.current = computeScrollStep(el.clientWidth);
    };
    syncStep();

    const ro = new ResizeObserver(syncStep);
    ro.observe(el);
    window.addEventListener('resize', syncStep);
    return () => {
      ro.disconnect();
      window.removeEventListener('resize', syncStep);
    };
  }, [isLarge]);

  const scroll = useCallback(
    (direction: 'left' | 'right') => {
      const el = wrapperRef.current;
      if (!el) return;
      const step = direction === 'left' ? -stepRef.current : stepRef.current;
      el.scrollBy({ left: step, behavior: 'smooth' });
      setTimeout(updateScrollState, 220);
    },
    [updateScrollState]
  );

  useEffect(() => {
    const el = wrapperRef.current;
    if (!el) return;
    updateScrollState();

    let rafId = 0;
    const onScroll = () => {
      if (rafId) return;
      rafId = requestAnimationFrame(() => {
        updateScrollState();
        rafId = 0;
      });
    };
    const onResize = debounce(updateScrollState, 150);

    el.addEventListener('scroll', onScroll);
    window.addEventListener('resize', onResize);
    return () => {
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', onResize);
      if (rafId) cancelAnimationFrame(rafId);
    };
  }, [updateScrollState]);

  return {
    scrollWrapperRef: wrapperRef,
    canScrollLeft,
    canScrollRight,
    scrollExamples: scroll,
  };
}
